import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import CourseQuestionList from './CourseQuestionList';
import './CourseForm.css';

const emptyInstructorRow = () => ({
    displayName: '',
    title: '',
    bio: '',
    imageUrl: '',
    linkedIn: '',
    sortOrder: 0,
    useImageUpload: false
});

const CourseForm = ({ course = null, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        category: '',
        imageUrl: '',
        pageImageUrl: '',
        durationHours: '',
        // New Udemy-style fields
        rating: '',
        reviewCount: '',
        studentsEnrolled: '',
        isBestSeller: false,
        language: 'English',
        learningOutcomes: '',
        requirements: '',
        targetAudience: '',
        fullDescription: '',
        // Course Type fields
        courseType: 'RECORDED', // LIVE or RECORDED
        // Live course specific fields
        startDate: '',
        endDate: '',
        enrollmentDeadline: '',
        maxStudents: '',
        currentEnrollments: '',
        sessionFrequency: '',
        totalSessions: '',
        isCohortBased: false,
        cohortNumber: '',
        // Discount/Offer fields
        originalPrice: '',
        isOnSale: false,
        discountPercentage: '',
        discountStartDate: '',
        discountEndDate: '',
        offerDescription: '',
        installmentEnabled: false,
        installmentCount: '3',
        registrationFee: '',
        installmentNotes: ''
    });
    const [instructors, setInstructors] = useState([emptyInstructorRow()]);
    const [uploadingInstructorIndex, setUploadingInstructorIndex] = useState(null);
    const [instructorImagePreviews, setInstructorImagePreviews] = useState({});
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showSectionForm, setShowSectionForm] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);
    const [showQuestions, setShowQuestions] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [useImageUpload, setUseImageUpload] = useState(false);
    const [pageImagePreview, setPageImagePreview] = useState(null);
    const [uploadingPageImage, setUploadingPageImage] = useState(false);
    const [usePageImageUpload, setUsePageImageUpload] = useState(false);
    const [sectionFormData, setSectionFormData] = useState({
        title: '',
        description: '',
        sectionOrder: '',
        isFree: false,
        isPublic: false
    });

    useEffect(() => {
        if (course) {
            // Parse arrays from pipe-separated strings
            const parseLearningOutcomes = (data) => {
                if (!data) return '';
                if (Array.isArray(data)) return data.join('\n');
                return data.split('|').join('\n');
            };

            if (course.courseInstructors && course.courseInstructors.length > 0) {
                setInstructors(
                    [...course.courseInstructors]
                        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        .map((ci, i) => ({
                            displayName: ci.displayName || '',
                            title: ci.title || '',
                            bio: ci.bio || '',
                            imageUrl: ci.imageUrl || '',
                            linkedIn: ci.linkedIn || '',
                            sortOrder: ci.sortOrder ?? i,
                            useImageUpload: false
                        }))
                );
            } else if (
                course.instructor ||
                course.instructorTitle ||
                course.instructorBio ||
                course.instructorImageUrl ||
                course.instructorLinkedIn
            ) {
                setInstructors([
                    {
                        displayName: course.instructor || '',
                        title: course.instructorTitle || '',
                        bio: course.instructorBio || '',
                        imageUrl: course.instructorImageUrl || '',
                        linkedIn: course.instructorLinkedIn || '',
                        sortOrder: 0,
                        useImageUpload: false
                    }
                ]);
            } else {
                setInstructors([emptyInstructorRow()]);
            }
            setInstructorImagePreviews({});
            setPageImagePreview(null);

            setFormData({
                title: course.title || '',
                description: course.description || '',
                price: course.price ? course.price.toString() : '',
                category: course.category || '',
                imageUrl: course.imageUrl || '',
                pageImageUrl: course.pageImageUrl || '',
                useImageUpload: false,
                usePageImageUpload: false,
                durationHours: course.durationHours ? course.durationHours.toString() : '',
                // New fields
                rating: course.rating ? course.rating.toString() : '',
                reviewCount: course.reviewCount ? course.reviewCount.toString() : '',
                studentsEnrolled: course.studentsEnrolled ? course.studentsEnrolled.toString() : '',
                isBestSeller: course.isBestSeller || false,
                language: course.language || 'English',
                learningOutcomes: parseLearningOutcomes(course.learningOutcomes),
                requirements: parseLearningOutcomes(course.requirements),
                targetAudience: parseLearningOutcomes(course.targetAudience),
                fullDescription: course.fullDescription || '',
                // Course type fields
                courseType: course.courseType || 'RECORDED',
                startDate: course.startDate ? course.startDate.split('T')[0] + 'T' + course.startDate.split('T')[1]?.substring(0, 5) : '',
                endDate: course.endDate ? course.endDate.split('T')[0] + 'T' + course.endDate.split('T')[1]?.substring(0, 5) : '',
                enrollmentDeadline: course.enrollmentDeadline ? course.enrollmentDeadline.split('T')[0] + 'T' + course.enrollmentDeadline.split('T')[1]?.substring(0, 5) : '',
                maxStudents: course.maxStudents ? course.maxStudents.toString() : '',
                currentEnrollments: course.currentEnrollments ? course.currentEnrollments.toString() : '0',
                sessionFrequency: course.sessionFrequency || '',
                totalSessions: course.totalSessions ? course.totalSessions.toString() : '',
                isCohortBased: course.isCohortBased || false,
                cohortNumber: course.cohortNumber ? course.cohortNumber.toString() : '',
                // Discount fields
                originalPrice: course.originalPrice ? course.originalPrice.toString() : '',
                isOnSale: course.isOnSale || false,
                discountPercentage: course.discountPercentage ? course.discountPercentage.toString() : '',
                discountStartDate: course.discountStartDate ? course.discountStartDate.split('T')[0] + 'T' + course.discountStartDate.split('T')[1]?.substring(0, 5) : '',
                discountEndDate: course.discountEndDate ? course.discountEndDate.split('T')[0] + 'T' + course.discountEndDate.split('T')[1]?.substring(0, 5) : '',
                offerDescription: course.offerDescription || '',
                installmentEnabled: course.installmentEnabled === true,
                installmentCount: course.installmentCount != null ? String(course.installmentCount) : '3',
                registrationFee: course.registrationFee != null && course.registrationFee !== '' ? String(course.registrationFee) : '',
                installmentNotes: course.installmentNotes || ''
            });
            
            // Fetch sections if editing an existing course
            if (course.id) {
                fetchSections(course.id);
            }
        } else {
            setInstructors([emptyInstructorRow()]);
            setInstructorImagePreviews({});
            setPageImagePreview(null);
        }
    }, [course]);

    const fetchSections = async (courseId) => {
        try {
            console.log('Fetching sections for course:', courseId);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/course-sections/course/${courseId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            if (response.ok) {
                const sectionsData = await response.json();
                console.log('Sections fetched:', sectionsData);
                setSections(sectionsData.sort((a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0)));
            } else {
                console.error('Failed to fetch sections:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error fetching sections:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const validInstructors = instructors
                .map((row, idx) => ({ ...row, sortOrder: idx }))
                .filter((row) => row.displayName && row.displayName.trim());
            if (validInstructors.length === 0) {
                setError('Add at least one instructor with a name.');
                setLoading(false);
                return;
            }

            // Convert newline-separated strings to pipe-separated
            const convertToPipeSeparated = (text) => {
                if (!text) return '';
                return text.split('\n').filter(line => line.trim()).join('|');
            };

            const primary = validInstructors[0];
            const courseData = {
                title: formData.title,
                description: formData.description,
                instructor: validInstructors.map((r) => r.displayName.trim()).join(', '),
                price: parseFloat(formData.price) || 0,
                category: formData.category,
                imageUrl: formData.imageUrl || '',
                pageImageUrl: formData.pageImageUrl || '',
                durationHours: parseInt(formData.durationHours) || null,
                // New fields
                rating: formData.rating ? parseFloat(formData.rating) : null,
                reviewCount: formData.reviewCount ? parseInt(formData.reviewCount) : null,
                studentsEnrolled: formData.studentsEnrolled ? parseInt(formData.studentsEnrolled) : null,
                isBestSeller: formData.isBestSeller,
                language: formData.language || 'English',
                learningOutcomes: convertToPipeSeparated(formData.learningOutcomes),
                requirements: convertToPipeSeparated(formData.requirements),
                targetAudience: convertToPipeSeparated(formData.targetAudience),
                fullDescription: formData.fullDescription || '',
                instructorTitle: primary.title || '',
                instructorBio: primary.bio || '',
                instructorImageUrl: primary.imageUrl || '',
                instructorLinkedIn: primary.linkedIn || '',
                courseInstructors: validInstructors.map((row, idx) => ({
                    displayName: row.displayName.trim(),
                    title: row.title || '',
                    bio: row.bio || '',
                    imageUrl: row.imageUrl || '',
                    linkedIn: row.linkedIn || '',
                    sortOrder: idx
                })),
                // Course type fields
                courseType: formData.courseType,
                startDate: formData.startDate || null,
                endDate: formData.endDate || null,
                enrollmentDeadline: formData.enrollmentDeadline || null,
                maxStudents: formData.maxStudents ? parseInt(formData.maxStudents) : null,
                currentEnrollments: formData.currentEnrollments ? parseInt(formData.currentEnrollments) : 0,
                sessionFrequency: formData.sessionFrequency || '',
                totalSessions: formData.totalSessions ? parseInt(formData.totalSessions) : null,
                isCohortBased: formData.isCohortBased,
                cohortNumber: formData.cohortNumber ? parseInt(formData.cohortNumber) : null,
                // Discount/Offer fields
                originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
                isOnSale: formData.isOnSale || false,
                discountPercentage: formData.discountPercentage ? parseFloat(formData.discountPercentage) : null,
                discountStartDate: formData.discountStartDate || null,
                discountEndDate: formData.discountEndDate || null,
                offerDescription: formData.offerDescription || null,
                installmentEnabled: formData.installmentEnabled === true,
                installmentCount: formData.installmentCount ? parseInt(formData.installmentCount, 10) : 3,
                registrationFee: formData.registrationFee !== '' && formData.registrationFee != null
                    ? parseFloat(formData.registrationFee)
                    : null,
                installmentNotes: formData.installmentNotes || null,
                sections: sections // Include current sections
            };

            console.log('Saving course with data:', courseData);

            const url = course 
                ? `${API_URL}/api/courses/${course.id}`
                : `${API_URL}/api/courses`;
            
            const method = course ? 'PUT' : 'POST';

            console.log('Making request to:', url);
            console.log('Method:', method);

            const token = localStorage.getItem('token');
            if (!token) {
                setError('You must be logged in as an admin to perform this action');
                setLoading(false);
                return;
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(courseData),
            });

            console.log('Response status:', response.status);

            if (response.ok) {
                try {
                    const savedCourse = await response.json();
                    console.log('Course saved successfully:', savedCourse);
                    onSave(savedCourse);
                } catch (jsonError) {
                    console.log('JSON parsing error, but operation likely succeeded');
                    // Don't try to confirm immediately, just assume success
                    onSave(course); // Use the original course data
                }
            } else {
                const errorText = await response.text();
                console.error('Failed to save course. Status:', response.status);
                console.error('Error response:', errorText);
                setError(`Failed to save course: ${errorText}`);
            }
        } catch (error) {
            console.error('Network error saving course:', error);
            // Check if this is a chunked encoding error (which means the operation actually succeeded)
            if (error.message && error.message.includes('chunked')) {
                console.log('Chunked encoding error detected, but operation likely succeeded');
                // Try to fetch the updated course to confirm
                try {
                    const token = localStorage.getItem('token');
                    const confirmResponse = await fetch(`${API_URL}/api/courses/${course.id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        credentials: 'include'
                    });
                    if (confirmResponse.ok) {
                        const updatedCourse = await confirmResponse.json();
                        console.log('Course update confirmed:', updatedCourse);
                        onSave(updatedCourse);
                        return;
                    }
                } catch (confirmError) {
                    console.error('Error confirming course update:', confirmError);
                }
            }
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleImageFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
            return;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            setError('Image size exceeds 10MB. Please upload a smaller image.');
            return;
        }

        setUseImageUpload(true);
        setError('');

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Upload image
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('token');
            const uploadResponse = await fetch(`${API_URL}/api/upload/image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // DO NOT set Content-Type header - browser will set it automatically with boundary for FormData
                },
                body: formData
            });

            if (!uploadResponse.ok) {
                let errorMessage = 'Failed to upload image';
                try {
                    const errorData = await uploadResponse.json();
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (parseError) {
                    // If response is not JSON, try to get text
                    const errorText = await uploadResponse.text();
                    errorMessage = errorText || errorMessage;
                }
                console.error('Upload failed:', errorMessage, 'Status:', uploadResponse.status);
                throw new Error(errorMessage);
            }

            const uploadData = await uploadResponse.json();
            console.log('Image upload response:', uploadData);
            
            // Ensure we have a valid URL
            let imageUrl = uploadData.url;
            if (!imageUrl) {
                throw new Error('No URL returned from server');
            }
            
            // If it's a relative URL, make it absolute using API_URL
            if (imageUrl && imageUrl.startsWith('/')) {
                imageUrl = API_URL + imageUrl;
            }
            
            console.log('Setting imageUrl to:', imageUrl);
            // Only update imageUrl, preserve all other form data
            setFormData(prevFormData => ({
                ...prevFormData,
                imageUrl: imageUrl
            }));
        } catch (error) {
            console.error('Image upload error:', error);
            setError(`Failed to upload image: ${error.message}`);
            setImagePreview(null);
            setUseImageUpload(false);
        } finally {
            setUploadingImage(false);
        }
    };

    const updateInstructorRow = (index, patch) => {
        setInstructors((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
    };

    const addInstructorRow = () => {
        setInstructors((prev) => [...prev, { ...emptyInstructorRow(), sortOrder: prev.length }]);
    };

    const removeInstructorRow = (index) => {
        setInstructors((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
        setInstructorImagePreviews({});
    };

    const moveInstructorRow = (index, delta) => {
        setInstructors((prev) => {
            const j = index + delta;
            if (j < 0 || j >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[j]] = [next[j], next[index]];
            return next.map((row, i) => ({ ...row, sortOrder: i }));
        });
    };

    const handleInstructorImageFileChange = async (index, e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Image size exceeds 10MB. Please upload a smaller image.');
            return;
        }

        updateInstructorRow(index, { useImageUpload: true });
        setError('');

        const reader = new FileReader();
        reader.onloadend = () => {
            setInstructorImagePreviews((prev) => ({ ...prev, [index]: reader.result }));
        };
        reader.readAsDataURL(file);

        setUploadingInstructorIndex(index);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);

            const token = localStorage.getItem('token');
            const uploadResponse = await fetch(`${API_URL}/api/upload/image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataUpload
            });

            if (!uploadResponse.ok) {
                let errorMessage = 'Failed to upload image';
                try {
                    const errorData = await uploadResponse.json();
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (parseError) {
                    const errorText = await uploadResponse.text();
                    errorMessage = errorText || errorMessage;
                }
                console.error('Upload failed:', errorMessage, 'Status:', uploadResponse.status);
                throw new Error(errorMessage);
            }

            const uploadData = await uploadResponse.json();

            let imageUrl = uploadData.url;
            if (!imageUrl) {
                throw new Error('No URL returned from server');
            }

            if (imageUrl && imageUrl.startsWith('/')) {
                imageUrl = API_URL + imageUrl;
            }

            updateInstructorRow(index, { imageUrl });
        } catch (error) {
            console.error('Network error uploading instructor image:', error);
            setError(`Failed to upload instructor image: ${error.message}`);
            setInstructorImagePreviews((prev) => {
                const next = { ...prev };
                delete next[index];
                return next;
            });
        } finally {
            setUploadingInstructorIndex(null);
        }
    };

    const handleRemoveImage = () => {
        setImagePreview(null);
        setUseImageUpload(false);
        setFormData({
            ...formData,
            imageUrl: ''
        });
    };

    const handlePageImageFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('Image size exceeds 10MB. Please upload a smaller image.');
            return;
        }

        setUsePageImageUpload(true);
        setError('');

        const reader = new FileReader();
        reader.onloadend = () => {
            setPageImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        setUploadingPageImage(true);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);

            const token = localStorage.getItem('token');
            const uploadResponse = await fetch(`${API_URL}/api/upload/image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataUpload
            });

            if (!uploadResponse.ok) {
                let errorMessage = 'Failed to upload image';
                try {
                    const errorData = await uploadResponse.json();
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch (parseError) {
                    const errorText = await uploadResponse.text();
                    errorMessage = errorText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            const uploadData = await uploadResponse.json();
            let url = uploadData.url;
            if (!url) {
                throw new Error('No URL returned from server');
            }
            if (url.startsWith('/')) {
                url = API_URL + url;
            }
            setFormData((prev) => ({ ...prev, pageImageUrl: url }));
        } catch (error) {
            console.error('Page image upload error:', error);
            setError(`Failed to upload page image: ${error.message}`);
            setPageImagePreview(null);
            setUsePageImageUpload(false);
        } finally {
            setUploadingPageImage(false);
        }
    };

    const handleRemovePageImage = () => {
        setPageImagePreview(null);
        setUsePageImageUpload(false);
        setFormData((prev) => ({ ...prev, pageImageUrl: '' }));
    };

    const handleSectionFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSectionFormData({
            ...sectionFormData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleAddSection = () => {
        setEditingSection(null);
        setSectionFormData({
            title: '',
            description: '',
            sectionOrder: sections.length + 1,

            isFree: false
        });
        setShowSectionForm(true);
    };

    const handleEditSection = (section) => {
        setEditingSection(section);
        setSectionFormData({
            title: section.title || '',
            description: section.description || '',
            sectionOrder: section.sectionOrder || sections.length + 1,
            isFree: section.isFree || false,
            isPublic: section.isPublic || false
        });
        setShowSectionForm(true);
    };

    const handleSaveSection = async (e) => {
        e.preventDefault();
        
        if (!course || !course.id) {
            setError('Please save the course first before adding sections');
            return;
        }

        try {
            const sectionData = {
                ...sectionFormData,
                sectionOrder: parseInt(sectionFormData.sectionOrder),
                course: {
                    id: course.id
                }
            };

            console.log('Saving section:', sectionData);
            console.log('Course ID:', course.id);

            const url = editingSection 
                ? `${API_URL}/api/course-sections/${editingSection.id}`
                : `${API_URL}/api/course-sections`;
            
            const method = editingSection ? 'PUT' : 'POST';

            console.log('Making request to:', url);
            console.log('Method:', method);

            const token = localStorage.getItem('token');
            if (!token) {
                setError('You must be logged in as an admin to perform this action');
                return;
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(sectionData),
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            if (response.ok) {
                try {
                    const savedSection = await response.json();
                    console.log('Section saved successfully:', savedSection);
                    
                    // Section is now created with course association
                    
                    setShowSectionForm(false);
                    setEditingSection(null);
                    // Clear any previous errors
                    setError('');
                    // Small delay to ensure backend has processed the request
                    setTimeout(() => {
                        fetchSections(course.id);
                    }, 500);
                } catch (jsonError) {
                    console.log('JSON parsing error for section, but operation likely succeeded');
                    // Refresh sections to confirm
                    setTimeout(() => {
                        fetchSections(course.id);
                    }, 1000); // Increased delay
                    setShowSectionForm(false);
                    setEditingSection(null);
                    setError('');
                }
            } else {
                const errorText = await response.text();
                console.error('Failed to save section. Status:', response.status);
                console.error('Error response:', errorText);
                setError(`Failed to save section: ${errorText}`);
            }
        } catch (error) {
            console.error('Network error saving section:', error);
            // Check if this is a chunked encoding error (which means the operation actually succeeded)
            if (error.message && error.message.includes('chunked')) {
                console.log('Chunked encoding error detected, but section operation likely succeeded');
                // Refresh sections to confirm
                setTimeout(() => {
                    fetchSections(course.id);
                }, 500);
                setShowSectionForm(false);
                setEditingSection(null);
                setError('');
                return;
            }
            setError('Network error. Please try again.');
        }
    };

    const handleDeleteSection = async (sectionId) => {
        if (window.confirm('Are you sure you want to delete this section?')) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/course-sections/${sectionId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    credentials: 'include'
                });
                
                if (response.ok) {
                    fetchSections(course.id);
                } else {
                    setError('Failed to delete section');
                }
            } catch (error) {
                setError('Error deleting section');
            }
        }
    };

    const handleCancelSectionForm = () => {
        setShowSectionForm(false);
        setEditingSection(null);
    };

    const handleManageQuestions = (section) => {
        setSelectedSection(section);
        setShowQuestions(true);
    };

    const handleBackToSections = () => {
        setShowQuestions(false);
        setSelectedSection(null);
    };

    if (showSectionForm) {
        return (
            <div className="course-form-container">
                <div className="course-form-card">
                    <h2>{editingSection ? 'Edit Section' : 'Add New Section'}</h2>
                    
                    {error && <div className="error-message">{error}</div>}
                    
                    <form onSubmit={handleSaveSection}>
                        <div className="form-group">
                            <label htmlFor="sectionTitle">Section Title *</label>
                            <input
                                type="text"
                                id="sectionTitle"
                                name="title"
                                value={sectionFormData.title}
                                onChange={handleSectionFormChange}
                                required
                                placeholder="Enter section title"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="sectionDescription">Description</label>
                            <textarea
                                id="sectionDescription"
                                name="description"
                                value={sectionFormData.description}
                                onChange={handleSectionFormChange}
                                rows="3"
                                placeholder="Enter section description"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="sectionOrder">Order</label>
                                <input
                                    type="number"
                                    id="sectionOrder"
                                    name="sectionOrder"
                                    value={sectionFormData.sectionOrder}
                                    onChange={handleSectionFormChange}
                                    min="1"
                                    placeholder="1"
                                />
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="isFree">
                                    <input
                                        type="checkbox"
                                        id="isFree"
                                        name="isFree"
                                        checked={sectionFormData.isFree}
                                        onChange={handleSectionFormChange}
                                    />
                                    Free Section
                                </label>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="isPublic">
                                <input
                                    type="checkbox"
                                    id="isPublic"
                                    name="isPublic"
                                    checked={sectionFormData.isPublic}
                                    onChange={handleSectionFormChange}
                                />
                                📖 Make Public (visible to non-enrolled users for preview)
                            </label>
                            <small style={{display: 'block', marginLeft: '20px', color: '#666', marginTop: '4px'}}>
                                Check this to allow non-enrolled users to preview this section
                            </small>
                        </div>

                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="cancel-btn"
                                onClick={handleCancelSectionForm}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="save-btn"
                            >
                                {editingSection ? 'Update Section' : 'Add Section'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // Show question management if a section is selected
    if (showQuestions && selectedSection) {
        return (
            <div className="course-form-container">
                <div className="course-form-card">
                    <div className="question-management-header">
                        <button 
                            type="button" 
                            className="back-btn"
                            onClick={handleBackToSections}
                        >
                            ← Back to Sections
                        </button>
                        <h2>Manage Questions: {selectedSection.title}</h2>
                    </div>
                    <CourseQuestionList 
                        sectionId={selectedSection.id}
                        sectionTitle={selectedSection.title}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="course-form-container">
            <div className="course-form-card">
                <h2>{course ? 'Edit Course' : 'Create New Course'}</h2>
                
                {error && <div className="error-message">{error}</div>}
                
                <form onSubmit={handleSubmit}>
                    {/* Course Type Selection */}
                    <div className="form-section-header" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                        <h3 style={{color: 'white', margin: 0}}>📚 Course Type</h3>
                    </div>

                    <div className="form-group">
                        <label htmlFor="courseType" style={{fontSize: '16px', fontWeight: '600'}}>
                            Select Course Type *
                        </label>
                        <div style={{display: 'flex', gap: '20px', marginTop: '10px'}}>
                            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '15px 20px', border: '2px solid', borderColor: formData.courseType === 'RECORDED' ? '#667eea' : '#ddd', borderRadius: '8px', background: formData.courseType === 'RECORDED' ? '#f0f4ff' : 'white', flex: 1}}>
                                <input
                                    type="radio"
                                    name="courseType"
                                    value="RECORDED"
                                    checked={formData.courseType === 'RECORDED'}
                                    onChange={handleInputChange}
                                    style={{marginRight: '10px', width: '20px', height: '20px'}}
                                />
                                <div>
                                    <div style={{fontSize: '18px', fontWeight: 'bold'}}>🎬 Recorded Course</div>
                                    <div style={{fontSize: '13px', color: '#666', marginTop: '5px'}}>Pre-recorded videos and content</div>
                                </div>
                            </label>
                            <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '15px 20px', border: '2px solid', borderColor: formData.courseType === 'LIVE' ? '#667eea' : '#ddd', borderRadius: '8px', background: formData.courseType === 'LIVE' ? '#f0f4ff' : 'white', flex: 1}}>
                                <input
                                    type="radio"
                                    name="courseType"
                                    value="LIVE"
                                    checked={formData.courseType === 'LIVE'}
                                    onChange={handleInputChange}
                                    style={{marginRight: '10px', width: '20px', height: '20px'}}
                                />
                                <div>
                                    <div style={{fontSize: '18px', fontWeight: 'bold'}}>🔴 Live Course</div>
                                    <div style={{fontSize: '13px', color: '#666', marginTop: '5px'}}>Real-time interactive sessions</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="form-section-header">
                        <h3>Basic Information</h3>
                    </div>

                    <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="title">Course Title *</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter course title"
                            />
                        </div>
                    </div>

                    <div className="form-section-header" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '15px', borderRadius: '8px', marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px'}}>
                        <h3 style={{color: 'white', margin: 0}}>👨‍🏫 Instructors</h3>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={addInstructorRow}
                            style={{ background: 'white', color: '#764ba2', border: 'none', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
                        >
                            + Add instructor
                        </button>
                    </div>
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                        Add one or more instructors. Each needs a name; use order buttons to control how they appear on the course page.
                    </p>

                    {instructors.map((inst, index) => (
                        <div
                            key={index}
                            style={{
                                border: '1px solid #e0e0e0',
                                padding: '16px',
                                marginBottom: '16px',
                                borderRadius: '8px',
                                background: '#fafafa'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                <strong style={{ fontSize: '16px' }}>Instructor {index + 1}</strong>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        onClick={() => moveInstructorRow(index, -1)}
                                        disabled={index === 0}
                                        style={{ padding: '6px 10px', cursor: index === 0 ? 'not-allowed' : 'pointer' }}
                                    >
                                        ↑ Move up
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => moveInstructorRow(index, 1)}
                                        disabled={index === instructors.length - 1}
                                        style={{ padding: '6px 10px', cursor: index === instructors.length - 1 ? 'not-allowed' : 'pointer' }}
                                    >
                                        ↓ Move down
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removeInstructorRow(index)}
                                        disabled={instructors.length <= 1}
                                        style={{ padding: '6px 10px', color: '#c00', cursor: instructors.length <= 1 ? 'not-allowed' : 'pointer' }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor={`inst-name-${index}`}>Name *</label>
                                    <input
                                        type="text"
                                        id={`inst-name-${index}`}
                                        value={inst.displayName}
                                        onChange={(e) => updateInstructorRow(index, { displayName: e.target.value })}
                                        placeholder="Instructor name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor={`inst-title-${index}`}>Title / role</label>
                                    <input
                                        type="text"
                                        id={`inst-title-${index}`}
                                        value={inst.title}
                                        onChange={(e) => updateInstructorRow(index, { title: e.target.value })}
                                        placeholder="e.g. Senior Software Engineer"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor={`inst-li-${index}`}>LinkedIn URL</label>
                                <input
                                    type="url"
                                    id={`inst-li-${index}`}
                                    value={inst.linkedIn}
                                    onChange={(e) => updateInstructorRow(index, { linkedIn: e.target.value })}
                                    placeholder="https://linkedin.com/in/..."
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor={`inst-img-${index}`}>Profile image</label>
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '10px' }}>
                                        <input
                                            type="radio"
                                            name={`instructorImageSource-${index}`}
                                            checked={!inst.useImageUpload}
                                            onChange={() => updateInstructorRow(index, { useImageUpload: false })}
                                        />
                                        <span>Image URL</span>
                                    </label>
                                    <label style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                        <input
                                            type="radio"
                                            name={`instructorImageSource-${index}`}
                                            checked={inst.useImageUpload}
                                            onChange={() => updateInstructorRow(index, { useImageUpload: true })}
                                        />
                                        <span>Upload file</span>
                                    </label>
                                </div>
                                {!inst.useImageUpload ? (
                                    <input
                                        type="url"
                                        id={`inst-img-${index}`}
                                        value={inst.imageUrl}
                                        onChange={(e) => updateInstructorRow(index, { imageUrl: e.target.value })}
                                        placeholder="https://..."
                                    />
                                ) : (
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            onChange={(e) => handleInstructorImageFileChange(index, e)}
                                            disabled={uploadingInstructorIndex === index}
                                            style={{ marginBottom: '10px' }}
                                        />
                                        {uploadingInstructorIndex === index && (
                                            <div style={{ color: '#667eea', marginBottom: '10px' }}>Uploading…</div>
                                        )}
                                    </div>
                                )}
                                {(instructorImagePreviews[index] || inst.imageUrl) && (
                                    <div style={{ marginTop: '12px' }}>
                                        <div
                                            style={{
                                                position: 'relative',
                                                display: 'inline-block',
                                                border: '2px solid #ddd',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                width: '120px',
                                                height: '120px'
                                            }}
                                        >
                                            <img
                                                src={instructorImagePreviews[index] || inst.imageUrl}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    updateInstructorRow(index, { imageUrl: '' });
                                                    setInstructorImagePreviews((prev) => {
                                                        const next = { ...prev };
                                                        delete next[index];
                                                        return next;
                                                    });
                                                }}
                                                style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    background: 'rgba(255, 0, 0, 0.85)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor={`inst-bio-${index}`}>Bio</label>
                                <textarea
                                    id={`inst-bio-${index}`}
                                    value={inst.bio}
                                    onChange={(e) => updateInstructorRow(index, { bio: e.target.value })}
                                    rows="4"
                                    placeholder="Background and expertise (use blank lines between paragraphs)"
                                />
                            </div>
                        </div>
                    ))}

                    <div className="form-group">
                        <label htmlFor="description">Description *</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows="4"
                            placeholder="Enter course description"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="category">Category *</label>
                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select category</option>
                                <option value="Programming">Programming</option>
                                <option value="Web Development">Web Development</option>
                                <option value="Data Science">Data Science</option>
                                <option value="Mobile Development">Mobile Development</option>
                                <option value="DevOps">DevOps</option>
                                <option value="Design">Design</option>
                                <option value="Business">Business</option>
                                <option value="Marketing">Marketing</option>
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="price">Price ($) *</label>
                            <input
                                type="number"
                                id="price"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                required
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '16px', padding: '16px', background: '#f8f9ff', borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600 }}>
                            <input
                                type="checkbox"
                                name="installmentEnabled"
                                checked={formData.installmentEnabled}
                                onChange={(e) => setFormData({ ...formData, installmentEnabled: e.target.checked })}
                            />
                            Allow installment payments (learners can split GST-inclusive total)
                        </label>
                        <small style={{ color: '#555', display: 'block', marginTop: '8px', marginLeft: '28px' }}>
                            When enabled, the course page shows that installments are available, and learners can choose full pay or installments at checkout.
                        </small>
                        {formData.installmentEnabled && (
                            <>
                                <div className="form-group" style={{ marginTop: '12px' }}>
                                    <label htmlFor="registrationFee">Initial registration fee (₹, incl. GST) — optional</label>
                                    <input
                                        type="number"
                                        id="registrationFee"
                                        name="registrationFee"
                                        min={0}
                                        step="0.01"
                                        value={formData.registrationFee}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 5000 — first payment; leave empty for equal installments only"
                                    />
                                    <small style={{ color: '#666', display: 'block', marginTop: '6px' }}>
                                        If set, learners pay this amount first; the <strong>remaining</strong> course total (incl. GST) is split into the number of payments below.
                                    </small>
                                </div>
                                <div className="form-group" style={{ marginTop: '12px' }}>
                                    <label htmlFor="installmentCount">
                                        {formData.registrationFee !== '' && parseFloat(formData.registrationFee) > 0
                                            ? 'Payments after registration (1–24)'
                                            : 'Equal installments (2–24)'}
                                    </label>
                                    <input
                                        type="number"
                                        id="installmentCount"
                                        name="installmentCount"
                                        min={formData.registrationFee !== '' && parseFloat(formData.registrationFee) > 0 ? 1 : 2}
                                        max={24}
                                        value={formData.installmentCount}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="installmentNotes">Installment notes (optional, shown at checkout)</label>
                                    <textarea
                                        id="installmentNotes"
                                        name="installmentNotes"
                                        rows={2}
                                        value={formData.installmentNotes}
                                        onChange={handleInputChange}
                                        placeholder="e.g. Pay every 2 weeks; first payment unlocks full course access."
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="durationHours">Duration (hours)</label>
                            <input
                                type="number"
                                id="durationHours"
                                name="durationHours"
                                value={formData.durationHours}
                                onChange={handleInputChange}
                                min="1"
                                placeholder="Enter duration in hours"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="imageUrl">Listing image (courses list &amp; cards)</label>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '10px' }}>
                                    <input
                                        type="radio"
                                        name="imageSource"
                                        checked={!useImageUpload}
                                        onChange={() => {
                                            setUseImageUpload(false);
                                            setImagePreview(null);
                                        }}
                                    />
                                    <span>Use Image URL</span>
                                </label>
                                <label style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                    <input
                                        type="radio"
                                        name="imageSource"
                                        checked={useImageUpload}
                                        onChange={() => setUseImageUpload(true)}
                                    />
                                    <span>Upload Image</span>
                                </label>
                            </div>
                            
                            {!useImageUpload ? (
                                <input
                                    type="url"
                                    id="imageUrl"
                                    name="imageUrl"
                                    value={formData.imageUrl}
                                    onChange={handleInputChange}
                                    placeholder="https://example.com/image.jpg"
                                />
                            ) : (
                                <div>
                                    <input
                                        type="file"
                                        id="imageFile"
                                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                        onChange={handleImageFileChange}
                                        disabled={uploadingImage}
                                        style={{ marginBottom: '10px' }}
                                    />
                                    {uploadingImage && (
                                        <div style={{ color: '#667eea', marginBottom: '10px' }}>
                                            Uploading image...
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Image Preview */}
                            {(imagePreview || formData.imageUrl) && (
                                <div style={{ marginTop: '15px' }}>
                                    <div style={{ 
                                        position: 'relative', 
                                        display: 'inline-block',
                                        border: '2px solid #ddd',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        maxWidth: '100%'
                                    }}>
                                        <img 
                                            src={imagePreview || formData.imageUrl} 
                                            alt="Course preview" 
                                            style={{ 
                                                maxWidth: '400px', 
                                                maxHeight: '225px',
                                                width: 'auto',
                                                height: 'auto',
                                                display: 'block'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            style={{
                                                position: 'absolute',
                                                top: '5px',
                                                right: '5px',
                                                background: 'rgba(255, 0, 0, 0.8)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '30px',
                                                height: '30px',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            )}
                            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                                Recommended size: 1200x675px (16:9 aspect ratio). Max file size: 10MB
                            </small>
                        </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '16px', padding: '16px', background: '#f5f3ff', borderRadius: '8px', border: '1px solid #ddd6fe' }}>
                        <label htmlFor="pageImageUrl" style={{ fontWeight: 600 }}>Course page image (optional)</label>
                        <p style={{ fontSize: '13px', color: '#555', margin: '6px 0 12px' }}>
                            Shown at the top when learners open this course (displayed in <strong>4:5</strong> aspect ratio). Use a different image from the listing thumbnail above, or leave empty to reuse the listing image.
                        </p>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '10px' }}>
                                <input
                                    type="radio"
                                    name="pageImageSource"
                                    checked={!usePageImageUpload}
                                    onChange={() => {
                                        setUsePageImageUpload(false);
                                        setPageImagePreview(null);
                                    }}
                                />
                                <span>Use image URL</span>
                            </label>
                            <label style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <input
                                    type="radio"
                                    name="pageImageSource"
                                    checked={usePageImageUpload}
                                    onChange={() => setUsePageImageUpload(true)}
                                />
                                <span>Upload image</span>
                            </label>
                        </div>
                        {!usePageImageUpload ? (
                            <input
                                type="url"
                                id="pageImageUrl"
                                name="pageImageUrl"
                                value={formData.pageImageUrl}
                                onChange={handleInputChange}
                                placeholder="https://example.com/course-hero.jpg"
                            />
                        ) : (
                            <div>
                                <input
                                    type="file"
                                    id="pageImageFile"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                    onChange={handlePageImageFileChange}
                                    disabled={uploadingPageImage}
                                    style={{ marginBottom: '10px' }}
                                />
                                {uploadingPageImage && (
                                    <div style={{ color: '#667eea', marginBottom: '10px' }}>Uploading…</div>
                                )}
                            </div>
                        )}
                        {(pageImagePreview || formData.pageImageUrl) && (
                            <div style={{ marginTop: '15px' }}>
                                <div style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    border: '2px solid #ddd',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    maxWidth: '100%'
                                }}>
                                    <img
                                        src={pageImagePreview || formData.pageImageUrl}
                                        alt="Course page preview"
                                        style={{
                                            maxWidth: '400px',
                                            maxHeight: '225px',
                                            width: 'auto',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemovePageImage}
                                        style={{
                                            position: 'absolute',
                                            top: '5px',
                                            right: '5px',
                                            background: 'rgba(255, 0, 0, 0.8)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '30px',
                                            height: '30px',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Discount/Offer Section */}
                    <div className="form-section-header" style={{background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', padding: '15px', borderRadius: '8px', marginTop: '20px'}}>
                        <h3 style={{color: 'white', margin: 0}}>💰 Discount & Offers</h3>
                    </div>
                    
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={formData.isOnSale}
                                onChange={(e) => setFormData({...formData, isOnSale: e.target.checked})}
                            />
                            Enable Discount/Offer
                        </label>
                    </div>
                    
                    {formData.isOnSale && (
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="originalPrice">Original Price (before discount)</label>
                                <input
                                    type="number"
                                    id="originalPrice"
                                    name="originalPrice"
                                    value={formData.originalPrice}
                                    onChange={handleInputChange}
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g., 1000"
                                />
                                <small style={{color: '#999'}}>Leave empty if current price is original</small>
                            </div>
                            <div className="form-group">
                                <label htmlFor="discountPercentage">Discount Percentage (%)</label>
                                <input
                                    type="number"
                                    id="discountPercentage"
                                    name="discountPercentage"
                                    value={formData.discountPercentage}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    placeholder="e.g., 25 for 25% off"
                                />
                            </div>
                        </div>
                    )}
                    
                    {formData.isOnSale && formData.originalPrice && formData.discountPercentage && (
                        <div style={{
                            padding: '15px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '8px',
                            marginTop: '10px',
                            color: 'white'
                        }}>
                            <div style={{fontSize: '14px', marginBottom: '8px', opacity: 0.9}}>💰 Calculated Discounted Price:</div>
                            <div style={{fontSize: '24px', fontWeight: 'bold'}}>
                                ₹{(
                                    parseFloat(formData.originalPrice) * 
                                    (1 - parseFloat(formData.discountPercentage) / 100)
                                ).toFixed(2)}
                            </div>
                            <div style={{fontSize: '12px', marginTop: '5px', opacity: 0.8}}>
                                Original: ₹{parseFloat(formData.originalPrice).toFixed(2)} - 
                                {parseFloat(formData.discountPercentage)}% = 
                                ₹{(
                                    parseFloat(formData.originalPrice) * 
                                    (1 - parseFloat(formData.discountPercentage) / 100)
                                ).toFixed(2)}
                            </div>
                        </div>
                    )}
                    
                    {formData.isOnSale && (
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="discountStartDate">Discount Start Date</label>
                                <input
                                    type="datetime-local"
                                    id="discountStartDate"
                                    name="discountStartDate"
                                    value={formData.discountStartDate}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="discountEndDate">Discount End Date</label>
                                <input
                                    type="datetime-local"
                                    id="discountEndDate"
                                    name="discountEndDate"
                                    value={formData.discountEndDate}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    )}
                    
                    {formData.isOnSale && (
                        <div className="form-group">
                            <label htmlFor="offerDescription">Offer Description</label>
                            <input
                                type="text"
                                id="offerDescription"
                                name="offerDescription"
                                value={formData.offerDescription}
                                onChange={handleInputChange}
                                placeholder="e.g., Limited Time Offer, Flash Sale, Early Bird"
                            />
                        </div>
                    )}

                    {/* Live Course Specific Fields */}
                    {formData.courseType === 'LIVE' && (
                        <>
                            <div className="form-section-header" style={{background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', padding: '15px', borderRadius: '8px', marginTop: '20px'}}>
                                <h3 style={{color: 'white', margin: 0}}>🔴 Live Course Details</h3>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="startDate">Course Start Date *</label>
                                    <input
                                        type="datetime-local"
                                        id="startDate"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        required={formData.courseType === 'LIVE'}
                                    />
                                    <small>When the live course begins</small>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="endDate">Course End Date *</label>
                                    <input
                                        type="datetime-local"
                                        id="endDate"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                        required={formData.courseType === 'LIVE'}
                                    />
                                    <small>When the live course ends</small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="enrollmentDeadline">Enrollment Deadline *</label>
                                    <input
                                        type="datetime-local"
                                        id="enrollmentDeadline"
                                        name="enrollmentDeadline"
                                        value={formData.enrollmentDeadline}
                                        onChange={handleInputChange}
                                        required={formData.courseType === 'LIVE'}
                                    />
                                    <small>Last date for enrollment</small>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="maxStudents">Maximum Students *</label>
                                    <input
                                        type="number"
                                        id="maxStudents"
                                        name="maxStudents"
                                        value={formData.maxStudents}
                                        onChange={handleInputChange}
                                        min="1"
                                        required={formData.courseType === 'LIVE'}
                                        placeholder="e.g., 50"
                                    />
                                    <small>Limited seats for live interaction</small>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="currentEnrollments">Currently Enrolled Students</label>
                                    <input
                                        type="number"
                                        id="currentEnrollments"
                                        name="currentEnrollments"
                                        value={formData.currentEnrollments}
                                        onChange={handleInputChange}
                                        min="0"
                                        placeholder="e.g., 15"
                                    />
                                    <small>
                                        {formData.maxStudents && formData.currentEnrollments 
                                            ? `${formData.currentEnrollments}/${formData.maxStudents} enrolled (${Math.round((formData.currentEnrollments / formData.maxStudents) * 100)}% full)`
                                            : 'Number of students currently enrolled'}
                                    </small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="totalSessions">Total Live Sessions *</label>
                                    <input
                                        type="number"
                                        id="totalSessions"
                                        name="totalSessions"
                                        value={formData.totalSessions}
                                        onChange={handleInputChange}
                                        min="1"
                                        required={formData.courseType === 'LIVE'}
                                        placeholder="e.g., 12"
                                    />
                                    <small>Number of live sessions</small>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="sessionFrequency">Session Frequency *</label>
                                    <select
                                        id="sessionFrequency"
                                        name="sessionFrequency"
                                        value={formData.sessionFrequency}
                                        onChange={handleInputChange}
                                        required={formData.courseType === 'LIVE'}
                                    >
                                        <option value="">Select frequency</option>
                                        <option value="Daily">Daily</option>
                                        <option value="Twice per week">Twice per week</option>
                                        <option value="Thrice per week">Thrice per week</option>
                                        <option value="Weekly">Weekly (once per week)</option>
                                        <option value="Bi-weekly">Bi-weekly (every 2 weeks)</option>
                                        <option value="Weekend">Weekends only</option>
                                        <option value="Custom">Custom schedule</option>
                                    </select>
                                    <small>How often sessions occur</small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="isCohortBased">
                                        <input
                                            type="checkbox"
                                            id="isCohortBased"
                                            name="isCohortBased"
                                            checked={formData.isCohortBased}
                                            onChange={handleInputChange}
                                            style={{width: 'auto', marginRight: '10px'}}
                                        />
                                        Cohort/Batch Based Course
                                    </label>
                                    <small>Enable if this course runs in specific batches</small>
                                </div>
                                
                                {formData.isCohortBased && (
                                    <div className="form-group">
                                        <label htmlFor="cohortNumber">Cohort/Batch Number</label>
                                        <input
                                            type="number"
                                            id="cohortNumber"
                                            name="cohortNumber"
                                            value={formData.cohortNumber}
                                            onChange={handleInputChange}
                                            min="1"
                                            placeholder="e.g., 1, 2, 3..."
                                        />
                                        <small>Cohort identifier (e.g., Batch 1, Batch 2)</small>
                                    </div>
                                )}
                            </div>

                            <div className="form-group" style={{background: '#fff3cd', padding: '15px', borderRadius: '8px', border: '1px solid #ffc107'}}>
                                <div style={{display: 'flex', alignItems: 'start', gap: '10px'}}>
                                    <span style={{fontSize: '24px'}}>💡</span>
                                    <div>
                                        <strong style={{color: '#856404'}}>Live Course Note:</strong>
                                        <p style={{margin: '5px 0 0 0', color: '#856404', fontSize: '14px'}}>
                                            You can schedule individual live sessions after creating the course. 
                                            No need to add videos or content initially - just describe what students will learn!
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* New Udemy-style Fields Section */}
                    <div className="form-section-header">
                        <h3>Course Statistics & Details</h3>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="rating">Rating (0-5)</label>
                            <input
                                type="number"
                                id="rating"
                                name="rating"
                                value={formData.rating}
                                onChange={handleInputChange}
                                min="0"
                                max="5"
                                step="0.1"
                                placeholder="4.5"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="reviewCount">Review Count</label>
                            <input
                                type="number"
                                id="reviewCount"
                                name="reviewCount"
                                value={formData.reviewCount}
                                onChange={handleInputChange}
                                min="0"
                                placeholder="1234"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="studentsEnrolled">Total Students Enrolled</label>
                            <input
                                type="number"
                                id="studentsEnrolled"
                                name="studentsEnrolled"
                                value={formData.studentsEnrolled}
                                onChange={handleInputChange}
                                min="0"
                                placeholder="15000"
                            />
                            <small>Total number of students who have enrolled (for display purposes)</small>
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="language">Language</label>
                            <select
                                id="language"
                                name="language"
                                value={formData.language}
                                onChange={handleInputChange}
                            >
                                <option value="English">English</option>
                                <option value="Spanish">Spanish</option>
                                <option value="French">French</option>
                                <option value="German">German</option>
                                <option value="Chinese">Chinese</option>
                                <option value="Japanese">Japanese</option>
                                <option value="Hindi">Hindi</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="isBestSeller">
                            <input
                                type="checkbox"
                                id="isBestSeller"
                                name="isBestSeller"
                                checked={formData.isBestSeller}
                                onChange={handleInputChange}
                                style={{width: 'auto', marginRight: '10px'}}
                            />
                            Mark as Bestseller
                        </label>
                    </div>

                    <div className="form-section-header">
                        <h3>Course Content Details</h3>
                    </div>

                    <div className="form-group">
                        <label htmlFor="learningOutcomes">What You'll Learn (one per line)</label>
                        <textarea
                            id="learningOutcomes"
                            name="learningOutcomes"
                            value={formData.learningOutcomes}
                            onChange={handleInputChange}
                            rows="6"
                            placeholder="Build real-world projects&#10;Master React and Node.js&#10;Deploy applications to production"
                        />
                        <small>Enter each learning outcome on a new line</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="requirements">Requirements (one per line)</label>
                        <textarea
                            id="requirements"
                            name="requirements"
                            value={formData.requirements}
                            onChange={handleInputChange}
                            rows="4"
                            placeholder="Basic programming knowledge&#10;Computer with internet connection&#10;No paid software required"
                        />
                        <small>Enter each requirement on a new line</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="targetAudience">Who This Course Is For (one per line)</label>
                        <textarea
                            id="targetAudience"
                            name="targetAudience"
                            value={formData.targetAudience}
                            onChange={handleInputChange}
                            rows="4"
                            placeholder="Aspiring web developers&#10;Students wanting to learn programming&#10;Career changers"
                        />
                        <small>Enter each target audience on a new line</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="fullDescription">Full Description</label>
                        <textarea
                            id="fullDescription"
                            name="fullDescription"
                            value={formData.fullDescription}
                            onChange={handleInputChange}
                            rows="8"
                            placeholder="Enter detailed course description with multiple paragraphs..."
                        />
                        <small>This will be displayed in the description section. Use double line breaks for paragraphs.</small>
                    </div>

                    {/* Course Sections Section */}
                    {course && course.id && (
                        <div className="sections-section">
                            <div className="sections-header">
                                <h3>Course Sections</h3>
                                <button 
                                    type="button" 
                                    className="add-section-btn"
                                    onClick={handleAddSection}
                                >
                                    + Add Section
                                </button>
                            </div>
                            
                            {sections.length === 0 ? (
                                <div className="no-sections">
                                    <p>No sections added yet. Click "Add Section" to get started.</p>
                                </div>
                            ) : (
                                <div className="sections-list">
                                    {sections.map(section => (
                                        <div key={section.id} className="section-item">
                                            <div className="section-info">
                                                <h4>{section.title}</h4>
                                                <p>{section.description}</p>
                                                <div className="section-details">
                                                    <span className="section-order">Order: {section.sectionOrder}</span>
                                                    {section.isFree && <span className="free-badge">Free</span>}
                                                    
                                                </div>
                                            </div>
                                            <div className="section-actions">
                                                <button 
                                                    type="button"
                                                    className="manage-questions-btn"
                                                    onClick={() => handleManageQuestions(section)}
                                                >
                                                    Questions
                                                </button>
                                                <button 
                                                    type="button"
                                                    className="edit-section-btn"
                                                    onClick={() => handleEditSection(section)}
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    type="button"
                                                    className="delete-section-btn"
                                                    onClick={() => handleDeleteSection(section.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="form-actions">
                        <button 
                            type="button" 
                            className="cancel-btn"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="save-btn"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (course ? 'Update Course' : 'Create Course')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CourseForm; 
